import type { BidBattle } from "@bidbattles/bloks/lib/rapace";
import { cn } from "@bidbattles/bloks/lib/ui";
import { formatCurrency } from "@bidbattles/bloks/lib/utils";
import { type ComponentProps, forwardRef } from "react";
import { Render, type RenderFunction } from "./ui/render";

const BidBattleImageWrapper = forwardRef<
	HTMLDivElement,
	React.ComponentProps<"div">
>(({ className, children, ...props }, ref) => {
	return (
		<div ref={ref} className={cn("relative", className)} {...props}>
			<div
				className="absolute top-0 left-0 right-0 h-2/5"
				style={{
					background:
						"linear-gradient(180deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.00) 100%)",
				}}
			/>
			<div
				className="absolute bottom-0 left-0 right-0 h-2/5"
				style={{
					background:
						"linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)",
				}}
			/>
			{children}
		</div>
	);
});

function BidBattleStatus({
	className,
	status,
	getLabel,
}: {
	className?: string;
	status: BidBattle["status"];
	getLabel: (status: BidBattle["status"]) => string;
}) {
	return (
		<span
			className={cn(
				"px-3 py-1 rounded-2xl bg-white text-gray-900 text-sm leading-5 font-semibold",
				status === "LIVE" && "text-red-700",
				className,
			)}
		>
			{getLabel(status)}
		</span>
	);
}

type BidBattleInfo = Pick<
	BidBattle,
	| "status"
	| "item_name"
	| "item_image_url"
	| "item_description"
	| "open_seats"
	| "total_seats"
	| "entry_price"
>;

function BidBattleCard({
	battle,
	className,
	render,
	labels,
}: {
	battle: BidBattleInfo;
	className?: string;
	render: RenderFunction<{ className: string }>;
	labels: {
		forStatus: React.ComponentProps<typeof BidBattleStatus>["getLabel"];
	} & ComponentProps<typeof BidBattleEntryInfo>["labels"];
}) {
	return (
		<Render
			render={render}
			className={cn(
				"flex flex-col select-none rounded-2xl shadow-none max-w-full",
				"focus-visible:shadow-xl focus-visible:shadow-gray-400",
				"focus-visible:outline-1 focus-visible:outline-offset-0 outline-gray-300",
				"ease-in-out duration-100",
				className,
			)}
		>
			<BidBattleImageWrapper className="rounded-t-2xl overflow-hidden">
				<BidBattleStatus
					className="absolute top-2 left-2"
					status={battle.status}
					getLabel={labels.forStatus}
				/>
				<img
					alt={battle.item_name}
					src={battle.item_image_url}
					className="w-full object-cover aspect-[1.2/1]"
					loading="lazy"
				/>
			</BidBattleImageWrapper>
			<div className="py-4 px-6 bg-card-background border-gray-300 rounded-b-2xl border-b border-l border-r grow flex flex-col gap-4 justify-between">
				<div>
					<p className="font-bold text-lg leading-7 text-gray-900">
						{battle.item_name}
					</p>
					<p className="leading-6 text-gray-900 text-ellipsis whitespace-nowrap overflow-hidden">
						{battle.item_description}
					</p>
				</div>
				<BidBattleEntryInfo battle={battle} labels={labels} />
			</div>
		</Render>
	);
	// });
}

function BidBattleEntryInfo({
	battle,
	labels,
}: {
	battle: BidBattleInfo;
	labels: {
		openSeats: string;
		entryPrice: string;
	};
}) {
	const totalSeats = String(battle.total_seats);
	return (
		<div className="flex justify-between text-gray-900">
			<div>
				<p className="text-xs text-gray-500 leading-4.5">{labels.openSeats}</p>
				<p>
					<span className="font-bold">
						{String(battle.total_seats - battle.open_seats).padStart(
							totalSeats.length,
							"0",
						)}
					</span>
					/{totalSeats}
				</p>
			</div>
			<div>
				<p className="text-xs text-gray-500 leading-4.5">{labels.entryPrice}</p>
				<p className="font-bold">{formatCurrency("EUR", battle.entry_price)}</p>
			</div>
		</div>
	);
}

export type { BidBattleInfo };
export {
	BidBattleImageWrapper,
	BidBattleStatus,
	BidBattleCard,
	BidBattleEntryInfo,
};
